import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';

import {
  signatureSettings,
  type SignatureProviderKey,
  type SignatureRequestMovement,
  type SignatureSettings,
} from '@/shared/api/resources';
import { useAuth } from '@/shared/auth/useAuth';
import { formatDateTime } from '@/shared/lib/format';
import { Alert } from '@/shared/ui/Alert';
import { Button } from '@/shared/ui/Button';
import { Card, CardBody, CardHeader } from '@/shared/ui/Card';
import { Field } from '@/shared/ui/Field';
import { Select } from '@/shared/ui/Select';
import { useToast } from '@/shared/ui/useToast';

import {
  ambienteDoProvedor,
  movimentos,
  PERMISSAO_CONFIGURAR_ASSINATURA,
} from './signatureRequestLabels';

/**
 * Aba "Configuracao" de /assinaturas (P-MIN-4): o provedor da empresa, o signatario
 * padrao da seguradora, o prazo e o pedido automatico. Ver
 * docs/ADR/0062-assinatura-por-provedor.md.
 *
 * A credencial do provedor fica no servidor, nunca aqui: a lista so traz os provedores
 * que a instalacao ligou. O pedido automatico fica desligado por padrao, porque a
 * validade da assinatura automatica em nome de pessoa fisica nao foi decidida (D27).
 */

interface Formulario {
  provider: SignatureProviderKey;
  nome: string;
  email: string;
  cpf: string;
  cpfAlterado: boolean;
  auth: 'email' | 'automatica';
  prazo: string;
  automaticos: SignatureRequestMovement[];
}

function doServidor(c: SignatureSettings): Formulario {
  return {
    provider: c.provider,
    nome: c.default_signer_name,
    email: c.default_signer_email,
    cpf: '',
    cpfAlterado: false,
    auth: c.default_signer_auth,
    prazo: String(c.deadline_days),
    automaticos: c.auto_movements,
  };
}

export function SignatureSettingsTab() {
  const consulta = useQuery({
    queryKey: ['signature-settings'],
    queryFn: ({ signal }) => signatureSettings.get(signal),
  });

  if (consulta.isError) {
    return (
      <Alert tone="danger" title="Nao foi possivel carregar a configuracao">
        {consulta.error.message}
      </Alert>
    );
  }
  if (!consulta.data) return <p className="text-sm text-ink-500">Carregando.</p>;
  // A chave refaz o formulario quando a configuracao gravada muda.
  return (
    <FormularioDaConfiguracao
      key={consulta.data.updated_at ?? 'padrao'}
      configuracao={consulta.data}
    />
  );
}

function FormularioDaConfiguracao({
  configuracao: c,
}: {
  configuracao: SignatureSettings;
}) {
  const { can } = useAuth();
  const toast = useToast();
  const queryClient = useQueryClient();
  const podeAlterar = can(PERMISSAO_CONFIGURAR_ASSINATURA);
  const [form, setForm] = useState<Formulario>(() => doServidor(c));

  const gravar = useMutation({
    mutationFn: (f: Formulario) =>
      signatureSettings.update({
        provider: f.provider,
        default_signer_name: f.nome,
        default_signer_email: f.email,
        ...(f.cpfAlterado ? { default_signer_document: f.cpf } : {}),
        default_signer_auth: f.auth,
        deadline_days: Number(f.prazo) || 30,
        auto_movements: f.automaticos,
      }),
    onSuccess: (gravada) => {
      toast.success('Configuracao de assinatura gravada.');
      queryClient.setQueryData(['signature-settings'], gravada);
    },
    onError: (error: Error) => toast.error(error.message),
  });

  const opcoes = [
    { value: 'nenhum', label: 'Nenhum (sem assinatura eletronica)' },
    ...c.providers.map((p) => ({
      value: p.key,
      label: `${p.name} (${ambienteDoProvedor[p.environment] ?? p.environment})`,
    })),
  ];
  // A escolha gravada que perdeu a credencial continua visivel, para a pessoa saber.
  if (c.provider !== 'nenhum' && !c.providers.some((p) => p.key === c.provider)) {
    opcoes.push({
      value: c.provider,
      label: `${c.provider} (sem credencial no servidor)`,
    });
  }
  const alterar = (parcial: Partial<Formulario>) => setForm({ ...form, ...parcial });
  // O CPF gravado e da pessoa gravada: trocar o nome ou o e-mail sem informar o CPF
  // apaga o anterior no servidor (ADR 0079, R5-M02).
  const outraPessoa =
    form.nome.trim() !== c.default_signer_name ||
    form.email.trim().toLowerCase() !== c.default_signer_email.toLowerCase();
  const cpfSai =
    c.default_signer_document_masked !== '' && !form.cpfAlterado && outraPessoa;

  return (
    <Card>
      <CardHeader
        title="Configuracao da assinatura eletronica"
        description="Provedor da empresa, signatario padrao da seguradora, prazo e pedido automatico."
      />
      <CardBody className="flex flex-col gap-4">
        {!c.provider_ready ? (
          <Alert tone="warning" title="Provedor sem credencial nesta instalacao">
            O provedor escolhido nao esta ligado no servidor. Os pedidos ficam recusados
            ate a credencial ser configurada ou outro provedor ser escolhido.
          </Alert>
        ) : null}
        {c.providers.length === 0 ? (
          <Alert tone="info" title="Nenhum provedor ligado nesta instalacao">
            A credencial do provedor de assinatura fica no servidor. Enquanto ela nao
            for configurada, so a coleta interna esta disponivel.
          </Alert>
        ) : null}

        <div className="grid gap-4 sm:grid-cols-2">
          <Select
            label="Provedor de assinatura"
            options={opcoes}
            value={form.provider}
            disabled={!podeAlterar}
            onChange={(event) =>
              alterar({ provider: event.target.value as SignatureProviderKey })
            }
          />
          <Field
            label="Prazo da assinatura em dias"
            type="number"
            min={1}
            max={90}
            value={form.prazo}
            disabled={!podeAlterar}
            onChange={(event) => alterar({ prazo: event.target.value })}
          />
          <Field
            label="Nome do signatario da seguradora"
            value={form.nome}
            disabled={!podeAlterar}
            onChange={(event) => alterar({ nome: event.target.value })}
          />
          <Field
            label="E-mail do signatario da seguradora"
            type="email"
            value={form.email}
            disabled={!podeAlterar}
            onChange={(event) => alterar({ email: event.target.value })}
          />
          {c.default_signer_document_masked && podeAlterar ? (
            <label className="flex items-center gap-2 text-sm text-ink-800 sm:col-span-2">
              <input
                type="checkbox"
                checked={form.cpfAlterado && form.cpf === ''}
                onChange={(event) =>
                  alterar({ cpf: '', cpfAlterado: event.target.checked })
                }
              />
              Remover o CPF gravado ({c.default_signer_document_masked})
            </label>
          ) : null}
          <Field
            label="CPF do signatario da seguradora"
            hint={
              cpfSai
                ? `O CPF gravado (${c.default_signer_document_masked}) e de outra pessoa e sera apagado. Informe o CPF do novo signatario, se houver.`
                : c.default_signer_document_masked
                  ? `Gravado: ${c.default_signer_document_masked}. Deixe em branco para manter.`
                  : 'Opcional.'
            }
            value={form.cpf}
            disabled={!podeAlterar || (form.cpfAlterado && form.cpf === '')}
            onChange={(event) =>
              alterar({
                cpf: event.target.value,
                cpfAlterado: event.target.value !== '',
              })
            }
          />
          <Select
            label="Autenticacao do signatario da seguradora"
            options={[
              { value: 'email', label: 'Codigo por e-mail' },
              { value: 'automatica', label: 'Assinatura automatica (a decidir, D27)' },
            ]}
            value={form.auth}
            disabled={!podeAlterar}
            onChange={(event) =>
              alterar({ auth: event.target.value as 'email' | 'automatica' })
            }
          />
        </div>

        <fieldset className="flex flex-col gap-2 rounded-md border border-line p-3">
          <legend className="px-1 text-xs font-semibold text-ink-700">
            Pedido automatico na geracao do documento
          </legend>
          <p className="text-2xs text-ink-500">
            Desligado por padrao. Ligado, o documento gerado do movimento marcado vai
            sozinho ao provedor, com o signatario da seguradora. Vale para o documento
            gerado depois de ligar aquele movimento: acrescentar um movimento nao manda
            os documentos dele gerados antes.
          </p>
          <div className="flex flex-wrap gap-4">
            {movimentos.map((m) => (
              <label
                key={m.value}
                className="flex items-center gap-2 text-sm text-ink-800"
              >
                <input
                  type="checkbox"
                  disabled={!podeAlterar}
                  checked={form.automaticos.includes(m.value)}
                  onChange={(event) =>
                    alterar({
                      automaticos: event.target.checked
                        ? [...form.automaticos, m.value]
                        : form.automaticos.filter((x) => x !== m.value),
                    })
                  }
                />
                {m.label}
              </label>
            ))}
          </div>
          {c.auto_movements.length > 0 ? (
            <ul className="text-2xs text-ink-500">
              {movimentos
                .filter((m) => c.auto_movements.includes(m.value))
                .map((m) => {
                  const desde = c.auto_since_by_movement[m.value] ?? c.auto_since;
                  return (
                    <li key={m.value}>
                      {m.label}: ligado desde {desde ? formatDateTime(desde) : 'agora'}.
                    </li>
                  );
                })}
            </ul>
          ) : null}
        </fieldset>

        {c.saved ? (
          <p className="text-2xs text-ink-500">
            Alterada por {c.changed_label || 'nao informado'} em{' '}
            {formatDateTime(c.updated_at)}.
          </p>
        ) : null}

        {podeAlterar ? (
          <div>
            <Button loading={gravar.isPending} onClick={() => gravar.mutate(form)}>
              Gravar configuracao
            </Button>
          </div>
        ) : null}
      </CardBody>
    </Card>
  );
}
